import * as React from "react";
import dayjs, { Dayjs } from "dayjs";
import { CPTextProps } from "../../types";
import { parseDate } from "shared/helpers";
import CPText from "../CPText/CPText";

interface DateTextProps extends Omit<CPTextProps, "children"> {
  /** The date value to format. Accepts a native `Date`, a `dayjs` object, or an ISO date string. Displays `"N/A"` when undefined or invalid. */
  date: Date | Dayjs | string | undefined;
  /** dayjs-compatible format string. Defaults to `"MM/DD/YYYY"`. */
  format?: string;
}

/**
 * Formats and renders a date value using a `CPText` component.
 * Displays `"N/A"` when the date is undefined or cannot be parsed.
 *
 * @example
 * ```tsx
 * <DateText date={user.createdAt} />
 * <DateText date="2024-01-15" format="MMMM D, YYYY" />
 * ```
 */
const DateText: React.FC<DateTextProps> = (props) => {
  const format = props.format ?? "MM/DD/YYYY";

  const getDateStr = React.useCallback(
    (val: Date | Dayjs | string | number | undefined, format: string) => {
      const d = parseDate(val);
      return d.isValid() ? d.format(format) : "N/A";
    },
    [],
  );

  return (
    <CPText style={{ fontSize: "1em" }}>
      {getDateStr(props.date, format)}
    </CPText>
  );
};

export default DateText;
